import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { DataTable } from '@/components/CustomTable'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import AppDialog, { ModalActions } from '@/components/CustomModal'
import Loading from '@/components/Loading'
import { Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import type { ColumnDef } from '@tanstack/react-table'

import {
  useContainersQuery,
  useCreateContainerMutation,
  useUpdateContainerMutation,
  useDeleteContainerMutation,
} from '@/hooks/useContainers'
import { useDepotSelectQuery } from '@/hooks/useDepots'
import type { Container } from '@/types/backend'

type MaybeList<T> = T[] | { data?: T[] } | null | undefined
function toArray<T>(v: MaybeList<T>): T[] {
  if (!v) return []
  return Array.isArray(v) ? v : v.data ?? []
}

export default function ContainersPage() {
  const navigate = useNavigate()

  const [searchParams, setSearchParams] = useSearchParams()
  const initialDepotId = searchParams.get('depotId') ?? undefined
  const [depotIdFilter, setDepotIdFilter] = useState<string | undefined>(
    initialDepotId
  )

  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    if (depotIdFilter) next.set('depotId', depotIdFilter)
    else next.delete('depotId')
    setSearchParams(next)
  }, [depotIdFilter])

  const [search, _setSearch] = useState('')
  const { data: depotOptions } = useDepotSelectQuery(true)

  const {
    data: list,
    status,
    isFetching,
  } = useContainersQuery({ depotId: depotIdFilter }, true)

  const containers = useMemo<Container[]>(
    () => toArray<Container>(list as MaybeList<Container>),
    [list]
  )

  const depotNameById = useMemo<Record<string, string>>(
    () =>
      Object.fromEntries(
        (depotOptions ?? []).map((d) => [d.id, d.name])
      ) as Record<string, string>,
    [depotOptions]
  )

  // --- filtres client-side
  const filtered: Container[] = useMemo(() => {
    const q = search.trim().toLowerCase()
    return containers.filter((c) => {
      const depotName = depotNameById[c.depotId] ?? ''
      const okSearch =
        !q ||
        c.name?.toLowerCase().includes(q) ||
        c.description?.toLowerCase().includes(q) ||
        c.code?.toLowerCase().includes(q) ||
        depotName.toLowerCase().includes(q)
      return okSearch
    })
  }, [containers, search, depotNameById])

  // --- modales/états
  const [open, setOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [editing, setEditing] = useState<Container | null>(null)
  const [form, setForm] = useState<{
    name: string
    description: string
    depotId: string
    isDefault: 'YES' | 'NO'
  }>({ name: '', description: '', depotId: '', isDefault: 'NO' })

  // --- mutations
  const createMut = useCreateContainerMutation()
  const updateMut = useUpdateContainerMutation(editing?.id || '')
  const deleteMut = useDeleteContainerMutation()

  // --- colonnes
  const columns = useMemo<ColumnDef<Container>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nom',
        cell: ({ row }) => (
          <button
            onClick={() => navigate(`/containers/${row.original.id}`)}
            className='font-medium underline-offset-2 hover:underline'
          >
            {row.original.name}
          </button>
        ),
      },
      {
        accessorKey: 'depotId',
        header: 'Dépôt',
        cell: ({ row }) => depotNameById[row.original.depotId] ?? '—',
      },
      {
        accessorKey: 'isDefault',
        header: 'Défaut',
        cell: ({ row }) => (row.original.isDefault ? '★ Oui' : '—'),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => row.original.description ?? '—',
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className='flex items-center gap-3'>
            <Pencil
              size={18}
              className='cursor-pointer text-green-600 mr-4'
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setEditing(row.original)
                setForm({
                  name: row.original.name ?? '',
                  description: row.original.description ?? '',
                  depotId: row.original.depotId ?? '',
                  isDefault: row.original.isDefault ? 'YES' : 'NO',
                })
                setOpen(true)
              }}
            />
            <Trash2
              size={18}
              className='cursor-pointer text-red-600 ml-4'
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setPendingDeleteId(row.original.id)
                setConfirmOpen(true)
              }}
            />
          </div>
        ),
      },
    ],
    [navigate, depotNameById]
  )

  // --- actions
  const openCreate = () => {
    setEditing(null)
    setForm({
      name: '',
      description: '',
      depotId: depotIdFilter ?? '',
      isDefault: 'NO',
    })
    setOpen(true)
  }

  const submit = async () => {
    const name = form.name.trim()
    const description = form.description.trim() || undefined
    const depotId = form.depotId || undefined
    const isDefault = form.isDefault === 'YES'

    if (!name) return toast.error('Nom requis')
    if (!depotId) return toast.error('Dépôt requis')

    try {
      if (editing) {
        await updateMut.mutateAsync({ name, description, depotId, isDefault })
        toast.success('Conteneur modifié')
      } else {
        await createMut.mutateAsync({ name, description, depotId, isDefault })
        toast.success('Conteneur créé')
      }
      setOpen(false)
    } catch (e: any) {
      toast.error('Erreur', {
        description: e?.response?.data?.message ?? 'Action échouée',
      })
    }
  }

  const confirmDelete = async () => {
    if (!pendingDeleteId) return
    try {
      await deleteMut.mutateAsync(pendingDeleteId)
      toast.success('Conteneur supprimé')
      setConfirmOpen(false)
      setPendingDeleteId(null)
    } catch (e: any) {
      toast.error('Suppression échouée', {
        description: e?.response?.data?.message,
      })
    }
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between gap-3'>
        <h3 className='text-2xl font-semibold'>Conteneurs</h3>
        <div className='flex items-center gap-2'>
          <Button onClick={openCreate}>Ajouter</Button>
        </div>
      </div>

      {/* Outils : recherche locale + filtre dépôt */}
      <div className='flex flex-wrap items-center gap-3'>
        <Select
          value={depotIdFilter ?? 'ALL'}
          onValueChange={(v) => setDepotIdFilter(v === 'ALL' ? undefined : v)}
        >
          <SelectTrigger className='w-[220px]'>
            <SelectValue placeholder='Filtrer par dépôt' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='ALL'>Tous les dépôts</SelectItem>
            {(depotOptions ?? []).map((d) => (
              <SelectItem key={d.id} value={d.id}>
                {d.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {status === 'pending' ? (
        <Loading />
      ) : (
        <>
          <div className='text-sm text-muted-foreground'>
            Total: <b>{filtered.length}</b>
          </div>
          <DataTable<Container, unknown>
            columns={columns}
            data={filtered}
            rowClickable
            onRowClick={(row) => navigate(`/containers/${row.id}`)}
          />
          <div className='text-xs text-muted-foreground'>
            {isFetching ? 'Actualisation…' : null}
          </div>
        </>
      )}

      {/* Modale CRÉER/ÉDITER */}
      <AppDialog
        open={open}
        onOpenChange={setOpen}
        title={editing ? 'Modifier un conteneur' : 'Ajouter un conteneur'}
        description={
          editing
            ? 'Modifiez les informations du conteneur.'
            : 'Remplissez les informations du nouveau conteneur.'
        }
        asForm
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
        footer={
          <ModalActions
            onConfirm={submit}
            confirmLabel='Valider'
            confirmLoading={createMut.isPending || updateMut.isPending}
          />
        }
      >
        <div className='w-full px-4 py-2 space-y-3'>
          <Field label='Nom'>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder='Ex: Allée A3'
            />
          </Field>

          <Field label='Dépôt'>
            <Select
              value={form.depotId || ''}
              onValueChange={(v) => setForm((f) => ({ ...f, depotId: v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder='Choisir…' />
              </SelectTrigger>
              <SelectContent>
                {(depotOptions ?? []).map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label='Description (optionnel)'>
            <Input
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
              placeholder='Ex: Étagère du fond'
            />
          </Field>

          <Field label='Par défaut ?'>
            <Select
              value={form.isDefault}
              onValueChange={(v: 'YES' | 'NO') =>
                setForm((f) => ({ ...f, isDefault: v }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='NO'>Non</SelectItem>
                <SelectItem value='YES'>Oui</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
      </AppDialog>

      {/* Modale SUPPRIMER */}
      <AppDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title='Supprimer le conteneur'
        description='Cette action est irréversible.'
        footer={
          <ModalActions
            confirmVariant='destructive'
            confirmLabel='Supprimer'
            confirmLoading={deleteMut.isPending}
            onConfirm={confirmDelete}
          />
        }
      >
        <div className='px-4 py-2 text-sm'>
          Voulez-vous vraiment supprimer ce conteneur ?
        </div>
      </AppDialog>
    </div>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className='grid gap-1.5'>
      <Label>{label}</Label>
      {children}
    </div>
  )
}
